import { useUserInfo } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { useCallback, useEffect, useState } from "react";

import { GROUP_CHAT_DRAFT_LOCAL_STORAGE_KEY } from "@/constants";
import { IDraftGroupCharacter } from "@/types";
import { typedLocalStorageService } from "@/utils/localStorageRe";

const logger = appLogger.withTag("useGroupChatDrafts");

export const useGroupChatDrafts = () => {
  const { onlyUserId: userId } = useUserInfo();
  const [groupChatDrafts, setGroupChatDrafts] = useState<IDraftGroupCharacter[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * `typedLocalStorageService` appends its own `_<userId>` suffix, so the key below resolves to
   * `draft_group_chat_<userId>_<userId>`. That double suffix is the key every existing group chat
   * draft is already stored under, and the suffix the service appends is its own copy of the user
   * id, which is not always the one this hook sees. Dropping the suffix here would orphan those
   * drafts, so this stays as-is and every read and write in this hook goes through it.
   */
  const getStorageKey = useCallback(() => {
    return userId ? `${GROUP_CHAT_DRAFT_LOCAL_STORAGE_KEY}_${userId}` : GROUP_CHAT_DRAFT_LOCAL_STORAGE_KEY;
  }, [userId]);

  const loadDrafts = useCallback(() => {
    try {
      const storageKey = getStorageKey();
      const storedData = typedLocalStorageService.getItem(storageKey);
      const draftData = JSON.parse(storedData || "{}");
      const drafts = Object.values(draftData) as IDraftGroupCharacter[];
      setGroupChatDrafts(drafts);
      return drafts;
    } catch (error) {
      logger.error("Error loading group chat drafts:", error);
      setGroupChatDrafts([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [getStorageKey]);

  const saveDraft = useCallback(
    (draft: IDraftGroupCharacter) => {
      try {
        const storageKey = getStorageKey();
        const storedData = typedLocalStorageService.getItem(storageKey);
        const existingDrafts = JSON.parse(storedData || "{}");
        const draftId = draft.id || draft.character_id;

        if (!draftId) {
          logger.error("Draft must have an id or character_id");
          return false;
        }

        // Set default title if missing
        if (!draft.title) {
          draft.title = new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }

        const updatedDrafts = {
          ...existingDrafts,
          [draftId]: draft,
        };

        typedLocalStorageService.setItem(storageKey, JSON.stringify(updatedDrafts));
        loadDrafts();
        return true;
      } catch (error) {
        logger.error("Error saving group chat draft:", error);
        return false;
      }
    },
    [getStorageKey, loadDrafts],
  );

  const deleteDraft = useCallback(
    (draftId: string) => {
      try {
        const storageKey = getStorageKey();
        const storedData = typedLocalStorageService.getItem(storageKey);
        const existingDrafts = JSON.parse(storedData || "{}");

        if (existingDrafts[draftId]) {
          delete existingDrafts[draftId];
          typedLocalStorageService.setItem(storageKey, JSON.stringify(existingDrafts));
          loadDrafts();
          return true;
        }
        return false;
      } catch (error) {
        logger.error("Error deleting group chat draft:", error);
        return false;
      }
    },
    [getStorageKey, loadDrafts],
  );

  const getDraftById = useCallback(
    (draftId: string): IDraftGroupCharacter | undefined => {
      try {
        const storageKey = getStorageKey();
        const storedData = typedLocalStorageService.getItem(storageKey);
        const existingDrafts = JSON.parse(storedData || "{}");
        return existingDrafts[draftId] as IDraftGroupCharacter | undefined;
      } catch (error) {
        logger.error("Error getting draft by id:", error);
        return undefined;
      }
    },
    [getStorageKey],
  );

  useEffect(() => {
    if (userId) {
      loadDrafts();
    }
  }, [userId, loadDrafts]);

  return {
    groupChatDrafts,
    isLoading,
    saveDraft,
    deleteDraft,
    getDraftById,
    reloadDrafts: loadDrafts,
  };
};

export default useGroupChatDrafts;
